import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'treffpunkt_admin_token';

export const tokenStorage = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set: (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch {} },
  clear: () => { try { localStorage.removeItem(TOKEN_KEY); } catch {} },
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Request interceptor: attach Authorization: Bearer <token> if we have one.
// This is the PRIMARY auth mechanism — works reliably across all domains/proxies,
// even when browser cookies are blocked by CDN, third-party-cookie policies, or
// strict SameSite rules on the production domain. Cookies remain as a secondary
// fallback (backend accepts either).
api.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: on 401 for admin-only endpoints, clear token and redirect to /login.
// MUST NOT trigger on /auth/me (silent probe) or /public/* (anonymous visitors).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAdminEndpoint = url.includes('/admin/');
    if (status === 401 && isAdminEndpoint) {
      tokenStorage.clear();
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth ──
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) => api.put('/auth/password', { current_password, new_password }),
};

// ── Public ──
export const publicApi = {
  getMenu: () => api.get('/public/menu'),
  getEvents: () => api.get('/public/events'),
  getImages: (section) => section ? api.get(`/public/images/${section}`) : api.get('/public/images'),
  getContent: () => api.get('/public/content'),
  getContact: () => api.get('/public/contact'),
};

// ── Admin Menu ──
export const menuApi = {
  getCategories: () => api.get('/admin/menu/categories'),
  createCategory: (data) => api.post('/admin/menu/categories', data),
  updateCategory: (id, data) => api.put(`/admin/menu/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/menu/categories/${id}`),
  reorderCategories: (ids) => api.post('/admin/menu/categories-reorder', { ids }),
  getItems: (categoryId) => api.get('/admin/menu/items', { params: { category_id: categoryId } }),
  createItem: (data) => api.post('/admin/menu/items', data),
  updateItem: (id, data) => api.put(`/admin/menu/items/${id}`, data),
  deleteItem: (id) => api.delete(`/admin/menu/items/${id}`),
  reorderItems: (ids) => api.post('/admin/menu/items-reorder', { ids }),
};

// ── Admin Events ──
export const eventsApi = {
  getAll: () => api.get('/admin/events'),
  create: (data) => api.post('/admin/events', data),
  update: (id, data) => api.put(`/admin/events/${id}`, data),
  delete: (id) => api.delete(`/admin/events/${id}`),
};

// ── Admin Images ──
export const imagesApi = {
  getAll: () => api.get('/admin/images'),
  create: (data) => api.post('/admin/images', data),
  update: (id, data) => api.put(`/admin/images/${id}`, data),
  delete: (id) => api.delete(`/admin/images/${id}`),
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/upload', form);
  },
};

// ── Admin Content ──
export const contentApi = {
  getAll: () => api.get('/admin/content'),
  update: (id, value) => api.put(`/admin/content/${id}`, { value }),
};

// ── Admin Contact ──
export const contactApi = {
  get: () => api.get('/admin/contact'),
  update: (data) => api.put('/admin/contact', data),
};

export default api;
